import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { IoAlertCircleOutline, IoCheckmarkCircle, IoCloseOutline, IoInformationCircleOutline, IoSettingsOutline } from 'react-icons/io5';
import { MdFolder } from 'react-icons/md';
import FileList from './FileList';
import FileDetailPanel from './FileDetailPanel';
import { useFilingPreferences } from './FilingPreferencesSection';
import FolderView from './FolderView';
import LibraryHome from './LibraryHome';
import LibraryNav, { NAV_ITEMS } from './LibraryNav';
import LibrarySettingsView from './LibrarySettingsView';
import UnfiledView from './UnfiledView';
import { ConfirmDialog, FolderPickerDialog, NameDialog } from './LibraryDialogs';
import { createLibraryClient } from './libraryClient';
import {
  activeSuggestion,
  addOverride,
  buildFolderIndex,
  isDedupedName,
  nextLibrarySeq,
  pluralize,
  processingInfo,
  pruneOverrides,
  suggestionErrorText,
} from './fileLibrary';
import './FileBrowser.css';

const MOBILE_QUERY = '(max-width: 768px)';
const NOTICE_MS = 6000;
const EMPTY_TREE = { status: 'idle', folders: [], totalFiles: null, unfiledCount: null, error: null };
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function useIsMobile() {
  const read = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_QUERY).matches;
  const [isMobile, setIsMobile] = useState(read);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setIsMobile(mql.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange); else mql.addListener?.(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange); else mql.removeListener?.(onChange);
    };
  }, []);
  return isMobile;
}

const quote = (name) => `“${name}”`;

/**
 * The File Browser: a dashboard over the personal or company file library.
 * Home (search, stats, recent, folders) · All / Uploaded / Generated /
 * Email & research / Unfiled · the folder tree · a detail panel per file.
 */
export default function FileBrowserModal({
  isOpen,
  onClose,
  onAttachFile,
  dataUserId,
  ownerUserId,
  viewingUserLabel,
  workspaceContext,
  onOpenConversation,
  // User-level Atlas preferences (MainLayout). Without them the Library
  // settings page leaves out its Company mode card.
  atlasUserPreferences,
  atlasUserPreferencesSaving,
  onUpdateAtlasUserPreferences,
}) {
  const isMobile = useIsMobile();
  const companyId = workspaceContext?.companyId || workspaceContext?.availableCompanyId || null;
  const companyName = workspaceContext?.companyName || 'Company';
  const hasCompany = Boolean(companyId);
  const defaultScope = workspaceContext?.isCompanyScope && companyId ? 'company' : 'personal';
  const [scope, setScope] = useState(defaultScope);
  const isCompany = scope === 'company' && hasCompany;
  const scopeKey = isCompany ? `company:${companyId}` : 'personal';
  const contextUserId = dataUserId || ownerUserId || null;

  const fileWorkspaceContext = useMemo(() => (isCompany
    ? { ...workspaceContext, scopeType: 'company', mode: 'company', isCompanyScope: true, companyId }
    : { ...workspaceContext, scopeType: 'user', mode: 'personal', isCompanyScope: false, companyId: null }
  ), [companyId, isCompany, workspaceContext]);

  const legacyRef = useRef({});
  const client = useMemo(() => createLibraryClient({
    dataUserId: contextUserId,
    workspaceContext: fileWorkspaceContext,
    companyId,
    legacy: legacyRef.current,
  }), [companyId, contextUserId, fileWorkspaceContext]);

  const [view, setView] = useState({ id: 'home' });
  const [emailGroup, setEmailGroup] = useState(null);
  const [tree, setTree] = useState(EMPTY_TREE);
  const [stats, setStats] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [detailRevision, setDetailRevision] = useState(0);
  const [notice, setNotice] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [overrides, setOverrides] = useState({});
  const [expanded, setExpanded] = useState({});
  const [attachStatus, setAttachStatus] = useState({});
  const [dragFiles, setDragFiles] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  // Folder suggestions being accepted / dismissed: { [fileId]: 'accept' | 'dismiss' }.
  const [suggestionBusy, setSuggestionBusy] = useState({});
  const suggestionBusyRef = useRef(suggestionBusy);
  suggestionBusyRef.current = suggestionBusy;

  const modalRef = useRef(null);
  const noticeTimerRef = useRef(null);
  const scopeKeyRef = useRef(scopeKey);
  scopeKeyRef.current = scopeKey;
  const dialogRef = useRef(dialog);
  dialogRef.current = dialog;
  // Writes in flight (move, delete, rename, merge, …): the backdrop and Escape
  // don't close the browser while one is running.
  const opsRef = useRef(0);
  const [, setOpsInFlight] = useState(0);
  const backdropPressRef = useRef(false);

  const folderIndex = useMemo(() => buildFolderIndex(tree.folders), [tree.folders]);

  // --- session reset / data loading ---

  useLayoutEffect(() => {
    if (!isOpen) return;
    setScope(defaultScope);
    setView({ id: 'home' });
    setEmailGroup(null);
    setSelectedFile(null);
    setNotice(null);
    setDialog(null);
    setOverrides({});
    setExpanded({});
    setAttachStatus({});
    setSuggestionBusy({});
    setTree(EMPTY_TREE);
    setStats(null);
    // Only when the browser opens; scope changes inside a session are the user's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => () => clearTimeout(noticeTimerRef.current), []);

  const showNotice = useCallback((kind, message, actions = []) => {
    clearTimeout(noticeTimerRef.current);
    setNotice({ id: Date.now(), kind, message, actions });
    if (kind !== 'error' && !actions.length) {
      noticeTimerRef.current = setTimeout(() => setNotice(null), NOTICE_MS);
    }
  }, []);

  const loadTree = useCallback(async () => {
    const key = scopeKey;
    setTree((prev) => ({ ...prev, status: 'loading', error: null }));
    try {
      const data = await client.tree();
      if (scopeKeyRef.current !== key) return;
      setTree({
        status: 'ready',
        folders: Array.isArray(data?.folders) ? data.folders : [],
        totalFiles: data?.total_files ?? null,
        unfiledCount: data?.unfiled_count ?? null,
        error: null,
      });
    } catch (err) {
      if (scopeKeyRef.current !== key) return;
      setTree((prev) => ({ ...prev, status: 'error', error: err?.message || 'Folders could not be loaded.' }));
    }
  }, [client, scopeKey]);

  // Any library (personal or company) can turn auto-sort off: new files then
  // stay in Unfiled with a suggested folder.
  const autoSort = stats?.auto_sort !== false;
  const [autoSortBusy, setAutoSortBusy] = useState(false);
  // Who files an unfiled file: in your own personal library, you.
  const theirs = !isCompany && !viewingUserLabel;
  const filedBy = theirs ? 'you file' : 'someone files';
  const applyAutoSort = useCallback(async (next) => {
    setAutoSortBusy(true);
    setStats((prev) => (prev ? { ...prev, auto_sort: next } : prev));
    try {
      await client.setAutoSort(next);
      if (next) {
        showNotice('success', 'Auto-sort is on. New files are filed into folders automatically.');
      } else {
        showNotice('success', `Auto-sort is off. New files will wait in Unfiled until ${filedBy} them.`, [{
          label: 'Open Unfiled',
          run: () => { setView({ id: 'unfiled' }); setSelectedFile(null); },
        }]);
      }
    } catch (err) {
      setStats((prev) => (prev ? { ...prev, auto_sort: !next } : prev));
      showNotice('error', `Couldn't change auto-sort: ${err?.message || 'please try again.'}`);
    } finally {
      setAutoSortBusy(false);
    }
  }, [client, filedBy, showNotice]);

  // Turning auto-sort off changes who does the filing, so it's confirmed in
  // the page first; turning it back on happens at once.
  const requestAutoSort = useCallback((next) => {
    if (autoSortBusy || next === autoSort) return;
    if (!next) setDialog({ type: 'auto-sort-off' });
    else applyAutoSort(true);
  }, [applyAutoSort, autoSort, autoSortBusy]);

  const loadStats = useCallback(async () => {
    const key = scopeKey;
    try {
      const data = await client.stats();
      if (scopeKeyRef.current === key) setStats(data || null);
    } catch {
      if (scopeKeyRef.current === key) setStats(null);
    }
  }, [client, scopeKey]);

  useEffect(() => {
    if (!isOpen) return;
    loadTree();
    loadStats();
  }, [isOpen, loadStats, loadTree]);

  const refreshLibrary = useCallback(({ lists = true } = {}) => {
    loadTree();
    loadStats();
    if (lists) setRefreshKey((k) => k + 1);
  }, [loadStats, loadTree]);

  // --- closing, Escape, focus ---

  const handleClose = useCallback(() => {
    setDialog(null);
    setSelectedFile(null);
    onClose?.();
  }, [onClose]);

  const isBusy = useCallback(() => opsRef.current > 0 || Boolean(dialogRef.current?.busy), []);

  const track = useCallback(async (work) => {
    opsRef.current += 1;
    setOpsInFlight(opsRef.current);
    try {
      return await work();
    } finally {
      opsRef.current -= 1;
      setOpsInFlight(opsRef.current);
    }
  }, []);

  // Filing preferences, per library while the browser is open: an unsaved
  // edit survives switching views, and is dropped when the browser closes or
  // shows another user's or company's library.
  const {
    entry: filingPrefs,
    load: loadFilingPrefs,
    change: changeFilingPrefs,
    discard: discardFilingPrefs,
    save: saveFilingPrefs,
    discardAll: discardAllFilingPrefs,
    hasUnsaved: hasUnsavedFilingPrefs,
  } = useFilingPreferences({
    client,
    scopeKey,
    resetKey: isOpen ? `${contextUserId || ''}|${companyId || ''}` : null,
    track,
    showNotice,
  });

  // The close button, Escape and the backdrop: unsaved filing preferences are
  // confirmed in the page before the browser closes.
  const requestClose = useCallback(() => {
    if (hasUnsavedFilingPrefs()) {
      setDialog({ type: 'discard-filing-prefs' });
      return;
    }
    handleClose();
  }, [handleClose, hasUnsavedFilingPrefs]);

  // "Use personal memories in company mode" is a user-level preference that
  // MainLayout holds (also in Settings → Atlas Settings). A failed save says so.
  const updateUserPreferences = useCallback(async (patch) => {
    try {
      await onUpdateAtlasUserPreferences(patch);
    } catch (err) {
      showNotice('error', `Couldn't save this setting: ${err?.message || 'please try again.'}`);
    }
  }, [onUpdateAtlasUserPreferences, showNotice]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.activeElement;
    modalRef.current?.focus();
    return () => {
      if (previous && typeof previous.focus === 'function' && document.contains(previous)) previous.focus();
    };
  }, [isOpen]);

  // Keyboard: Escape closes the innermost layer; Tab stays inside the browser
  // (inside an open dialog when there is one), even when focus fell to <body>.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (event) => {
      if (event.key === 'Tab') {
        const root = modalRef.current;
        if (!root) return;
        const layer = root.querySelector('.fl-dialog') || root;
        const focusables = Array.from(layer.querySelectorAll(FOCUSABLE));
        const active = document.activeElement;
        if (!focusables.length) {
          event.preventDefault();
          (layer === root ? root : layer).focus?.();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!layer.contains(active) || active === layer) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        } else if (event.shiftKey && active === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
        return;
      }
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (dialogRef.current) {
        event.preventDefault();
        if (!dialogRef.current.busy) setDialog(null);
        return;
      }
      if (isBusy()) return;
      requestClose();
    };
    // Focus that lands outside the browser (a click or script behind it) comes back.
    const onFocusIn = (event) => {
      const root = modalRef.current;
      if (!root || root.contains(event.target)) return;
      const layer = root.querySelector('.fl-dialog');
      ((layer && layer.querySelector(FOCUSABLE)) || root).focus();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [isBusy, isOpen, requestClose]);

  // When a dialog or the detail panel closes, the element that had focus is
  // usually gone; put focus back on what opened it (or the browser itself).
  const focusBack = useCallback((candidates) => {
    const root = modalRef.current;
    if (!root) return;
    const active = document.activeElement;
    if (active && active !== document.body && active !== document.documentElement && root.contains(active)) return;
    const target = candidates.find((el) => el && el.isConnected && root.contains(el) && !el.disabled);
    (target || root).focus();
  }, []);

  const dialogOpenerRef = useRef(null);
  const dialogWasOpenRef = useRef(false);
  useLayoutEffect(() => {
    const open = Boolean(dialog);
    if (open && !dialogWasOpenRef.current) dialogOpenerRef.current = document.activeElement;
    if (!open && dialogWasOpenRef.current) focusBack([dialogOpenerRef.current]);
    dialogWasOpenRef.current = open;
  }, [dialog, focusBack]);

  const detailOpenerRef = useRef({ element: null, fileId: null });
  const detailWasOpenRef = useRef(false);
  useLayoutEffect(() => {
    const open = Boolean(selectedFile);
    if (open && !detailWasOpenRef.current) {
      detailOpenerRef.current = { element: document.activeElement, fileId: selectedFile.file_id };
    } else if (open) {
      detailOpenerRef.current = { ...detailOpenerRef.current, fileId: selectedFile.file_id };
    }
    if (!open && detailWasOpenRef.current) {
      const { element, fileId } = detailOpenerRef.current;
      const row = fileId && modalRef.current
        ? modalRef.current.querySelector(`[data-file-id="${CSS.escape ? CSS.escape(fileId) : fileId}"] .fl-row-name`)
        : null;
      focusBack([row, element]);
    }
    detailWasOpenRef.current = open;
  }, [focusBack, selectedFile]);

  // --- navigation ---

  const expandAncestors = useCallback((folderId) => {
    setExpanded((prev) => {
      const next = { ...prev };
      let current = folderIndex.byId.get(folderId);
      const seen = new Set();
      while (current?.parent_folder_id && !seen.has(current.parent_folder_id)) {
        seen.add(current.parent_folder_id);
        next[current.parent_folder_id] = true;
        current = folderIndex.byId.get(current.parent_folder_id);
      }
      return next;
    });
  }, [folderIndex]);

  const selectView = useCallback((next) => {
    setView(next);
    setSelectedFile(null);
    if (next.id === 'folder' && next.folderId) expandAncestors(next.folderId);
  }, [expandAncestors]);

  const openFolder = useCallback((folderId) => {
    if (!folderId) return;
    selectView({ id: 'folder', folderId });
  }, [selectView]);

  const switchScope = (next) => {
    const normalized = next === 'company' && hasCompany ? 'company' : 'personal';
    if (normalized === scope) return;
    setScope(normalized);
    setView({ id: 'home' });
    setSelectedFile(null);
    setDialog(null);
    setNotice(null);
    setOverrides({});
    setExpanded({});
    setSuggestionBusy({});
    setTree(EMPTY_TREE);
    setStats(null);
  };

  // --- file actions ---

  const patchFile = useCallback((fileId, patch) => {
    const seq = nextLibrarySeq();
    setOverrides((prev) => addOverride(prev, fileId, patch, seq));
    setSelectedFile((prev) => (prev?.file_id === fileId ? { ...prev, ...patch } : prev));
  }, []);

  // Fresh rows from the server replace local patches for those files.
  const handleFreshFiles = useCallback((fileIds, fetchSeq) => {
    setOverrides((prev) => pruneOverrides(prev, fileIds, fetchSeq));
  }, []);

  const handleDownload = useCallback((file, blob) => {
    Promise.resolve()
      .then(() => client.download(file, blob))
      .catch((err) => showNotice('error', `Couldn't download ${quote(file.filename)}: ${err?.message || 'download failed'}`));
  }, [client, showNotice]);

  const handleRenameFile = useCallback((file, requested) => track(async () => {
    const result = await client.renameFile(file.file_id, requested);
    const finalName = result?.filename || requested;
    patchFile(file.file_id, { filename: finalName });
    setDetailRevision((r) => r + 1);
    if (isDedupedName(requested, finalName)) {
      showNotice('info', `Renamed to ${quote(finalName)}: ${quote(requested)} is already used by another file.`);
    } else {
      showNotice('success', `Renamed to ${quote(finalName)}.`);
    }
    return result;
  }), [client, patchFile, showNotice, track]);

  const moveFiles = useCallback((files, targetFolderId) => track(async () => {
    const moved = [];
    const failed = [];
    for (const file of files) {
      try {
        const result = await client.moveFile(file.file_id, targetFolderId);
        moved.push({ file, result });
      } catch (err) {
        failed.push({ file, err });
      }
    }
    const targetPath = targetFolderId ? folderIndex.pathOf(targetFolderId) : '';
    moved.forEach(({ file, result }) => {
      const folders = Array.isArray(result?.folders)
        ? result.folders.map((f) => ({ folder_id: f.folder_id || null, name: f.name || '', path: f.path || f.name || '' }))
        : (targetFolderId ? [{ folder_id: targetFolderId, name: folderIndex.byId.get(targetFolderId)?.name || '', path: targetPath }] : []);
      patchFile(file.file_id, { folders });
    });
    const returnedPath = moved[0]?.result?.folders?.find((f) => f.folder_id === targetFolderId)?.path;
    const destination = targetFolderId ? (returnedPath || targetPath || 'the folder') : 'Unfiled';
    const movedText = moved.length === 1
      ? `Moved ${quote(moved[0].file.filename)} to ${destination}.`
      : moved.length ? `Moved ${pluralize(moved.length, 'file')} to ${destination}.` : '';
    if (failed.length) {
      const first = failed[0];
      const failedText = failed.length === 1
        ? `Couldn't move ${quote(first.file.filename)}: ${first.err?.message || 'move failed'}`
        : `${pluralize(failed.length, 'file')} couldn't be moved: ${first.err?.message || 'move failed'}`;
      showNotice('error', [movedText, failedText].filter(Boolean).join(' '));
    } else if (moved.length) {
      showNotice('success', movedText);
    }
    if (moved.length) {
      setDetailRevision((r) => r + 1);
      refreshLibrary();
    }
  }), [client, folderIndex, patchFile, refreshLibrary, showNotice, track]);

  const openMoveFiles = useCallback((files) => {
    if (!files?.length) return;
    setDialog({ type: 'move-files', files });
  }, []);

  const openDeleteFiles = useCallback((files) => {
    if (!files?.length) return;
    setDialog({ type: 'delete-files', files });
  }, []);

  const deleteFiles = useCallback((files) => track(async () => {
    const deleted = [];
    const failed = [];
    for (const file of files) {
      try {
        await client.deleteFile(file.file_id);
        deleted.push(file);
      } catch (err) {
        failed.push({ file, err });
      }
    }
    deleted.forEach((file) => patchFile(file.file_id, { deleted: true }));
    setSelectedFile((prev) => (prev && deleted.some((f) => f.file_id === prev.file_id) ? null : prev));
    if (failed.length) {
      const first = failed[0];
      showNotice('error', `Couldn't delete ${failed.length === 1 ? quote(first.file.filename) : pluralize(failed.length, 'file')}: ${first.err?.message || 'delete failed'}`);
    } else if (deleted.length) {
      showNotice('success', deleted.length === 1 ? `Deleted ${quote(deleted[0].filename)}.` : `Deleted ${pluralize(deleted.length, 'file')}.`);
    }
    // Lists drop the deleted rows in place (and page past them correctly).
    if (deleted.length) refreshLibrary({ lists: false });
  }), [client, patchFile, refreshLibrary, showNotice, track]);

  const handleAttach = useCallback((file) => {
    if (!onAttachFile) return;
    if (processingInfo(file).busy) {
      showNotice('error', `${quote(file.filename)} is still processing. Try again in a moment.`);
      return;
    }
    const result = onAttachFile({
      file_id: file.file_id,
      filename: file.filename,
      mime_type: file.mime_type,
      size_bytes: file.size_bytes,
      file_type: file.file_type,
      workspace_scope_type: isCompany ? 'company' : 'user',
      company_id: isCompany ? companyId : null,
    }) || { status: 'attached' };
    if (result.status === 'error') {
      showNotice('error', `Couldn't attach ${quote(file.filename)}: ${result.error || 'the chat is not available'}`);
      return;
    }
    setAttachStatus((prev) => ({ ...prev, [file.file_id]: result.status }));
    if (result.status === 'duplicate') showNotice('info', `${quote(file.filename)} is already in the chat composer.`);
    else if (result.status === 'queued') showNotice('success', `Added ${quote(file.filename)} to the chat. The tool panel was closed so you can send it.`);
    else showNotice('success', `Added ${quote(file.filename)} to the chat composer.`);
  }, [companyId, isCompany, onAttachFile, showNotice]);

  const handleShareFile = useCallback((file) => track(async () => {
    showNotice('info', `Sending ${quote(file.filename)} to the ${companyName} library…`);
    try {
      await client.shareFileToCompany(file.file_id);
      showNotice('success', `Added ${quote(file.filename)} to the ${companyName} library. Atlas is organizing it.`);
    } catch (err) {
      showNotice('error', `Couldn't send ${quote(file.filename)} to the company: ${err?.message || 'request failed'}`);
    }
  }), [client, companyName, showNotice, track]);

  // --- folder suggestions (unfiled files) ---

  const setSuggestionBusyFor = useCallback((fileId, value) => {
    setSuggestionBusy((prev) => {
      const next = { ...prev };
      if (value) next[fileId] = value; else delete next[fileId];
      return next;
    });
  }, []);

  // Accept one suggestion: the file moves into the suggested folder (the
  // server creates suggested new folders). The local patch takes the row out
  // of Unfiled, and keeps it out even if an older listing lands afterwards.
  const acceptOneSuggestion = useCallback(async (file) => {
    const suggestion = activeSuggestion(file) || file.suggestion || null;
    setSuggestionBusyFor(file.file_id, 'accept');
    try {
      const result = await client.acceptSuggestion(file.file_id);
      let folders = Array.isArray(result?.folders)
        ? result.folders.filter(Boolean).map((f) => ({ folder_id: f.folder_id || null, name: f.name || '', path: f.path || f.name || '' }))
        : [];
      if (!folders.length && suggestion?.path) {
        const parts = suggestion.path.split(' / ');
        folders = [{ folder_id: suggestion.folder_id || null, name: parts[parts.length - 1], path: suggestion.path }];
      }
      const path = (suggestion?.path && folders.find((f) => f.path === suggestion.path)?.path) || folders[0]?.path || suggestion?.path || 'the folder';
      patchFile(file.file_id, { folders, suggestion: null });
      return { ok: true, path };
    } catch (err) {
      // A suggestion the server can't act on any more is dead: stop offering it.
      if (err?.code === 'suggested_folder_gone' || err?.code === 'no_suggestion') patchFile(file.file_id, { suggestion: null });
      return { ok: false, err };
    } finally {
      setSuggestionBusyFor(file.file_id, null);
    }
  }, [client, patchFile, setSuggestionBusyFor]);

  const handleAcceptSuggestion = useCallback((file) => {
    if (suggestionBusyRef.current[file.file_id]) return Promise.resolve();
    return track(async () => {
      const outcome = await acceptOneSuggestion(file);
      if (outcome.ok) {
        showNotice('success', `Moved to ${outcome.path}.`);
        setDetailRevision((r) => r + 1);
        refreshLibrary();
        return;
      }
      const { err } = outcome;
      const moveTo = { label: 'Move to…', run: () => openMoveFiles([{ ...file, suggestion: null }]) };
      if (err?.code === 'suggested_folder_gone') {
        showNotice('error', `The folder Atlas suggested for ${quote(file.filename)} no longer exists.`, [moveTo]);
        loadTree();
      } else if (err?.code === 'no_suggestion') {
        showNotice('error', `Atlas no longer has a folder suggestion for ${quote(file.filename)}.`, [moveTo]);
        refreshLibrary();
      } else {
        showNotice('error', `Couldn't move ${quote(file.filename)}: ${suggestionErrorText(err)}`);
      }
    });
  }, [acceptOneSuggestion, loadTree, openMoveFiles, refreshLibrary, showNotice, track]);

  const handleDismissSuggestion = useCallback((file) => {
    if (suggestionBusyRef.current[file.file_id]) return Promise.resolve();
    return track(async () => {
      setSuggestionBusyFor(file.file_id, 'dismiss');
      try {
        await client.dismissSuggestion(file.file_id);
        patchFile(file.file_id, { suggestion: null });
      } catch (err) {
        showNotice('error', `Couldn't dismiss the folder suggestion for ${quote(file.filename)}: ${err?.message || 'request failed'}`);
      } finally {
        setSuggestionBusyFor(file.file_id, null);
      }
    });
  }, [client, patchFile, setSuggestionBusyFor, showNotice, track]);

  const openMoveAllSuggested = useCallback((files) => {
    const list = (files || []).filter((f) => activeSuggestion(f));
    if (!list.length) return;
    setDialog({ type: 'accept-suggestions', files: list, done: 0 });
  }, []);

  const suggestionHandlers = useMemo(() => ({
    busy: suggestionBusy,
    onAccept: handleAcceptSuggestion,
    onDismiss: handleDismissSuggestion,
  }), [handleAcceptSuggestion, handleDismissSuggestion, suggestionBusy]);

  // --- folder actions ---

  const confirmMerge = useCallback((source, targetId) => {
    const target = folderIndex.byId.get(targetId);
    setDialog({ type: 'confirm-merge', source, targetId, targetName: target?.name || 'the other folder', targetPath: folderIndex.pathOf(targetId) || target?.name || '' });
  }, [folderIndex]);

  const handleFolderConflict = useCallback((folder, err, attemptedName) => {
    const existingId = err?.body?.existing_folder_id;
    showNotice(
      'error',
      `A folder named ${quote(attemptedName)} already exists here.`,
      existingId ? [{ label: 'Merge into it', run: () => confirmMerge(folder, existingId) }] : [],
    );
  }, [confirmMerge, showNotice]);

  const folderActions = useMemo(() => ({
    createSubfolder: (folder) => setDialog({ type: 'create-folder', parentId: folder.folder_id, parentName: folder.name }),
    rename: (folder) => setDialog({ type: 'rename-folder', folder }),
    move: (folder) => setDialog({ type: 'move-folder', folder }),
    merge: (folder) => setDialog({ type: 'merge-folder', folder }),
    remove: (folder) => setDialog({ type: 'delete-folder', folder }),
    share: !isCompany && hasCompany ? (folder) => track(async () => {
      showNotice('info', `Sending ${quote(folder.name)} to the ${companyName} library…`);
      try {
        const result = await client.shareFolderToCompany(folder.folder_id);
        const n = Array.isArray(result?.files) ? result.files.length : 0;
        showNotice('success', `Added ${quote(folder.name)} to the ${companyName} library (${pluralize(n, 'file')}).`);
      } catch (err) {
        showNotice('error', `Couldn't send ${quote(folder.name)} to the company: ${err?.message || 'request failed'}`);
      }
    }) : null,
  }), [client, companyName, hasCompany, isCompany, showNotice, track]);

  const setDialogState = (patch) => setDialog((prev) => (prev ? { ...prev, ...patch } : prev));

  const runDialog = (work) => track(async () => {
    setDialogState({ busy: true, error: '' });
    try {
      await work();
    } catch (err) {
      setDialogState({ busy: false, error: err?.message || 'Something went wrong.' });
    }
  });

  const submitCreateFolder = (name) => runDialog(async () => {
    const parentId = dialogRef.current?.parentId || null;
    const result = await client.createFolder(name, parentId);
    setDialog(null);
    await loadTree();
    if (parentId) setExpanded((prev) => ({ ...prev, [parentId]: true }));
    if (result?.folder_id) openFolder(result.folder_id);
    if (result?.existing) {
      showNotice('info', `A folder named ${quote(result.name || name)} already exists here, so it was opened.`);
    } else {
      showNotice('success', `Created ${quote(result?.name || name)}.`);
    }
    loadStats();
  });

  const submitRenameFolder = (name) => runDialog(async () => {
    const folder = dialogRef.current?.folder;
    if (!folder) return;
    if (name === folder.name) {
      setDialog(null);
      return;
    }
    try {
      await client.renameFolder(folder.folder_id, name);
    } catch (err) {
      if (err?.status === 409) {
        setDialog(null);
        handleFolderConflict(folder, err, name);
        return;
      }
      throw err;
    }
    setDialog(null);
    showNotice('success', `Renamed folder to ${quote(name)}.`);
    refreshLibrary();
  });

  const submitMoveFolder = (parentId) => runDialog(async () => {
    const folder = dialogRef.current?.folder;
    if (!folder) return;
    try {
      await client.moveFolder(folder.folder_id, parentId);
    } catch (err) {
      setDialog(null);
      if (err?.status === 409) handleFolderConflict(folder, err, folder.name);
      else showNotice('error', `Couldn't move ${quote(folder.name)}: ${err?.message || 'move failed'}`);
      return;
    }
    setDialog(null);
    if (parentId) setExpanded((prev) => ({ ...prev, [parentId]: true }));
    showNotice('success', `Moved ${quote(folder.name)} to ${parentId ? folderIndex.pathOf(parentId) : 'the top level'}.`);
    refreshLibrary();
  });

  const runMerge = () => runDialog(async () => {
    const { source, targetId, targetPath } = dialogRef.current || {};
    if (!source || !targetId) return;
    let result;
    try {
      result = await client.mergeFolder(source.folder_id, targetId);
    } catch (err) {
      setDialog(null);
      showNotice('error', `Couldn't merge ${quote(source.name)}: ${err?.message || 'merge failed'}`);
      return;
    }
    setDialog(null);
    const files = Number(result?.moved_files) || 0;
    const folders = Number(result?.moved_folders) || 0;
    showNotice('success', `Merged ${quote(source.name)} into ${targetPath || 'the folder'}: ${pluralize(files, 'file')} and ${pluralize(folders, 'folder')} moved.`);
    openFolder(result?.target_folder_id || targetId);
    refreshLibrary();
  });

  const runDeleteFolder = () => runDialog(async () => {
    const folder = dialogRef.current?.folder;
    if (!folder) return;
    try {
      await client.deleteFolder(folder.folder_id);
    } catch (err) {
      setDialog(null);
      showNotice('error', `Couldn't delete ${quote(folder.name)}: ${err?.message || 'delete failed'}`);
      return;
    }
    setDialog(null);
    const removed = folderIndex.descendantsOf(folder.folder_id);
    removed.add(folder.folder_id);
    setView((prev) => (prev.id === 'folder' && removed.has(prev.folderId) ? { id: 'home' } : prev));
    showNotice('success', `Deleted ${quote(folder.name)}.`);
    refreshLibrary();
  });

  // One accept after another (each may create folders the next one reuses),
  // with progress in the dialog and one summary at the end.
  const runMoveAllSuggested = () => runDialog(async () => {
    const files = dialogRef.current?.files || [];
    let moved = 0;
    const failed = [];
    for (let i = 0; i < files.length; i += 1) {
      setDialogState({ done: i });
      // eslint-disable-next-line no-await-in-loop
      const outcome = await acceptOneSuggestion(files[i]);
      if (outcome.ok) moved += 1; else failed.push({ file: files[i], err: outcome.err });
    }
    setDialog(null);
    const movedText = moved ? `Moved ${pluralize(moved, 'file')} to their suggested folders.` : '';
    if (failed.length) {
      const first = failed[0];
      const failedText = failed.length === 1
        ? `Couldn't move ${quote(first.file.filename)}: ${suggestionErrorText(first.err)}.`
        : `${pluralize(failed.length, 'file')} couldn't be moved: ${suggestionErrorText(first.err)}.`;
      const gone = failed.filter(({ err }) => err?.code === 'suggested_folder_gone' || err?.code === 'no_suggestion').map(({ file }) => ({ ...file, suggestion: null }));
      showNotice('error', [movedText, failedText].filter(Boolean).join(' '), gone.length
        ? [{ label: 'Move to…', run: () => openMoveFiles(gone) }]
        : []);
    } else if (moved) {
      showNotice('success', movedText);
    }
    if (moved) setDetailRevision((r) => r + 1);
    refreshLibrary();
  });

  // --- drag and drop (desktop) ---

  const dragFilesRef = useRef(null);
  const handleDragFilesStart = useCallback((event, files) => {
    dragFilesRef.current = files;
    setDragFiles(files);
    if (event?.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      try { event.dataTransfer.setData('text/plain', files.map((f) => f.filename).join('\n')); } catch { /* some browsers */ }
    }
  }, []);
  const handleDragFilesEnd = useCallback(() => {
    dragFilesRef.current = null;
    setDragFiles(null);
    setDropTargetId(null);
  }, []);
  const handleDropOnTarget = useCallback((folderId) => {
    const files = dragFilesRef.current;
    handleDragFilesEnd();
    if (files?.length) moveFiles(files, folderId);
  }, [handleDragFilesEnd, moveFiles]);

  // --- rendering ---

  if (!isOpen) return null;

  const canShareToCompany = !isCompany && hasCompany;
  const listProps = {
    activeFileId: selectedFile?.file_id,
    isMobile,
    onOpenFile: setSelectedFile,
    onOpenFolder: openFolder,
    onMoveFiles: openMoveFiles,
    onDeleteFiles: openDeleteFiles,
    onDragFilesStart: handleDragFilesStart,
    onDragFilesEnd: handleDragFilesEnd,
    onFreshFiles: handleFreshFiles,
  };
  const commonList = { ...listProps, client, refreshKey, overrides };

  const groups = stats?.by_source_group || {};
  // Both by default; the segmented control narrows to one source.
  const activeEmailGroup = emailGroup || 'all';
  const emailResearchCount = (Number(groups.email) || 0) + (Number(groups.research) || 0);

  let main;
  if (view.id === 'home') {
    main = (
      <LibraryHome
        client={client}
        stats={stats}
        treeState={tree}
        folderIndex={folderIndex}
        refreshKey={refreshKey}
        overrides={overrides}
        listProps={listProps}
        onOpenFile={setSelectedFile}
        onOpenFolder={openFolder}
        onSelectView={selectView}
        onCreateFolder={() => setDialog({ type: 'create-folder', parentId: null })}
      />
    );
  } else if (view.id === 'folder') {
    main = (
      <FolderView
        folder={folderIndex.byId.get(view.folderId) || (tree.status === 'ready' ? null : { folder_id: view.folderId, name: 'Folder' })}
        folderIndex={folderIndex}
        client={client}
        refreshKey={refreshKey}
        overrides={overrides}
        listProps={listProps}
        folderActions={folderActions}
        onOpenFolder={openFolder}
        onGoHome={() => selectView({ id: 'home' })}
      />
    );
  } else if (view.id === 'email_research') {
    main = (
      <FileList
        {...commonList}
        key={`email:${activeEmailGroup}`}
        params={{ sourceGroup: activeEmailGroup === 'all' ? 'email,research' : activeEmailGroup }}
        title="Email & research"
        subtitle="Attachments Atlas saved from your email, and files it downloaded during web research."
        headerExtra={(
          <div className="fl-segmented" role="group" aria-label="Source">
            {[['all', 'All'], ['email', 'Email attachments'], ['research', 'Web research']].map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={activeEmailGroup === id ? 'is-on' : ''}
                aria-pressed={activeEmailGroup === id}
                onClick={() => setEmailGroup(id)}
              >
                {label}
                {id === 'all' && stats ? <span className="fl-segmented-count">{emailResearchCount}</span> : null}
                {id !== 'all' && groups[id] != null ? <span className="fl-segmented-count">{groups[id]}</span> : null}
              </button>
            ))}
          </div>
        )}
        emptyTitle={{ all: 'No email attachments or research downloads yet', email: 'No email attachments yet', research: 'No research downloads yet' }[activeEmailGroup]}
        emptyHint={{
          all: 'Attachments Atlas saves from your mailbox and files it downloads during web research show up here.',
          email: 'When Atlas saves an attachment from a connected mailbox it shows up here.',
          research: 'Files Atlas downloads during web research show up here.',
        }[activeEmailGroup]}
      />
    );
  } else if (view.id === 'unfiled') {
    main = (
      <UnfiledView
        key={`unfiled:${scopeKey}`}
        client={client}
        refreshKey={refreshKey}
        listProps={{ ...listProps, overrides }}
        suggestions={suggestionHandlers}
        bulkBusy={dialog?.type === 'accept-suggestions'}
        onMoveAllSuggested={openMoveAllSuggested}
        autoSuggest={!viewingUserLabel}
        title="Unfiled"
        subtitle={!autoSort
          ? `Auto-sort is off for this library, so new files land here until ${filedBy} them.`
          : 'Files that are not in any folder yet.'}
        emptyTitle="Every file is in a folder"
        emptyHint="Nice and tidy."
      />
    );
  } else if (view.id === 'settings') {
    // Company mode belongs to the signed-in user: only for company members,
    // not in an admin's view of someone else's library, and only when the
    // parent passes the preference in.
    const companyMode = hasCompany && !viewingUserLabel && atlasUserPreferences && typeof onUpdateAtlasUserPreferences === 'function'
      ? { preferences: atlasUserPreferences, saving: atlasUserPreferencesSaving, onUpdate: updateUserPreferences }
      : null;
    main = (
      <LibrarySettingsView
        key={`settings:${scopeKey}`}
        isCompany={isCompany}
        companyName={companyName}
        viewingUserLabel={viewingUserLabel}
        stats={stats}
        autoSortBusy={autoSortBusy}
        onSetAutoSort={requestAutoSort}
        filingPreferences={{
          entry: filingPrefs,
          onLoad: loadFilingPrefs,
          onChange: changeFilingPrefs,
          onSave: saveFilingPrefs,
          onDiscard: discardFilingPrefs,
        }}
        companyMode={companyMode}
      />
    );
  } else {
    const config = {
      all: { params: {}, title: 'All files', emptyTitle: 'No files yet', emptyHint: 'Upload a file in chat or ask Atlas to make one.' },
      uploaded: { params: { sourceGroup: 'uploaded' }, title: 'Uploaded', emptyTitle: 'No uploads yet', emptyHint: 'Files you add to a chat show up here.' },
      generated: { params: { sourceGroup: 'generated' }, title: 'Generated by Atlas', emptyTitle: 'Nothing generated yet', emptyHint: 'Spreadsheets, documents, decks and images Atlas makes show up here.' },
    }[view.id] || { params: {}, title: 'All files' };
    main = <FileList {...commonList} key={view.id} {...config} />;
  }

  const mobileValue = view.id === 'folder' ? `folder:${view.folderId}` : view.id;
  const mobileFolderOptions = [];
  const walk = (parentId, depth) => {
    (folderIndex.children.get(parentId) || []).forEach((folder) => {
      mobileFolderOptions.push({ value: `folder:${folder.folder_id}`, label: `${' '.repeat(depth)}${folder.name}${folderIndex.hintOf(folder.folder_id) ? ` (${folderIndex.hintOf(folder.folder_id)})` : ''}` });
      walk(folder.folder_id, depth + 1);
    });
  };
  walk('', 0);

  const dialogNode = (() => {
    if (!dialog) return null;
    const close = () => { if (!dialog.busy) setDialog(null); };
    switch (dialog.type) {
      case 'move-files': {
        const current = new Set();
        dialog.files.forEach((f) => (f.folders || []).forEach((folder) => folder.folder_id && current.add(folder.folder_id)));
        return (
          <FolderPickerDialog
            title={dialog.files.length === 1 ? `Move ${quote(dialog.files[0].filename)}` : `Move ${pluralize(dialog.files.length, 'file')}`}
            description="Moving takes the file out of its current folders."
            folderIndex={folderIndex}
            currentFolderIds={current}
            allowUnfiled
            busy={dialog.busy}
            onCancel={close}
            onConfirm={(target) => runDialog(async () => {
              await moveFiles(dialog.files, target);
              setDialog(null);
            })}
          />
        );
      }
      case 'move-folder': {
        const exclude = folderIndex.descendantsOf(dialog.folder.folder_id);
        exclude.add(dialog.folder.folder_id);
        return (
          <FolderPickerDialog
            title={`Move ${quote(dialog.folder.name)}`}
            folderIndex={folderIndex}
            excludeIds={exclude}
            currentFolderIds={new Set(dialog.folder.parent_folder_id ? [dialog.folder.parent_folder_id] : [])}
            allowRoot
            busy={dialog.busy}
            onCancel={close}
            onConfirm={submitMoveFolder}
          />
        );
      }
      case 'merge-folder': {
        const exclude = folderIndex.descendantsOf(dialog.folder.folder_id);
        exclude.add(dialog.folder.folder_id);
        return (
          <FolderPickerDialog
            title={`Merge ${quote(dialog.folder.name)} into…`}
            description="Pick the folder to keep. Everything in this folder moves into it."
            folderIndex={folderIndex}
            excludeIds={exclude}
            confirmLabel="Continue"
            onCancel={close}
            onConfirm={(targetId) => confirmMerge(dialog.folder, targetId)}
          />
        );
      }
      case 'confirm-merge':
        return (
          <ConfirmDialog
            title={`Merge ${quote(dialog.source.name)} into ${quote(dialog.targetName)}?`}
            message={`Its files and subfolders move into ${dialog.targetPath || quote(dialog.targetName)} (subfolders with the same name are combined), then ${quote(dialog.source.name)} is removed.`}
            confirmLabel="Merge"
            busy={dialog.busy}
            onCancel={close}
            onConfirm={runMerge}
          />
        );
      case 'auto-sort-off':
        return (
          <ConfirmDialog
            title="Turn off auto-sort?"
            message={isCompany
              ? `Atlas will stop filing new files in the ${companyName} library. Uploads and files Atlas makes will stay in Unfiled until someone on the team organizes them. Atlas still suggests a folder for each one, and anyone can accept it with one click. This applies to everyone in ${companyName}.`
              : theirs
                ? "Atlas will stop filing your new files into folders. Uploads and files Atlas makes will stay in Unfiled, and you'll need to organize them yourself. Atlas still suggests a folder for each one, and you can accept it with one click. You can turn auto-sort back on anytime."
                : `Atlas will stop filing new files in ${viewingUserLabel}'s library. They'll stay in Unfiled until they're organized by hand; Atlas still suggests a folder for each one.`}
            confirmLabel="Turn off auto-sort"
            onCancel={close}
            onConfirm={() => { setDialog(null); applyAutoSort(false); }}
          />
        );
      case 'discard-filing-prefs':
        return (
          <ConfirmDialog
            title="Discard your unsaved filing preferences?"
            confirmLabel="Discard"
            danger
            onCancel={close}
            onConfirm={() => { discardAllFilingPrefs(); handleClose(); }}
          />
        );
      case 'accept-suggestions': {
        const n = dialog.files.length;
        const creating = dialog.files.filter((f) => activeSuggestion(f)?.new_folders?.length).length;
        return (
          <ConfirmDialog
            title={`Move ${pluralize(n, 'file')} to their suggested folders?`}
            message={[
              n === 1 ? 'The file moves to the folder Atlas suggested for it.' : `Each of the ${n} files moves to the folder Atlas suggested for it.`,
              creating ? `${creating === 1 ? '1 suggestion creates a new folder' : `${creating} suggestions create new folders`}.` : '',
            ].filter(Boolean).join(' ')}
            confirmLabel={`Move ${pluralize(n, 'file')}`}
            busy={dialog.busy}
            status={dialog.busy ? `Moving ${Math.min((dialog.done || 0) + 1, n)} of ${n}…` : ''}
            onCancel={close}
            onConfirm={runMoveAllSuggested}
          />
        );
      }
      case 'delete-files':
        return (
          <ConfirmDialog
            title={dialog.files.length === 1 ? `Delete ${quote(dialog.files[0].filename)}?` : `Delete ${pluralize(dialog.files.length, 'file')}?`}
            message="Deleted files are removed from the library and every folder. Chats that used them keep their history."
            confirmLabel="Delete"
            danger
            busy={dialog.busy}
            onCancel={close}
            onConfirm={() => runDialog(async () => {
              await deleteFiles(dialog.files);
              setDialog(null);
            })}
          />
        );
      case 'delete-folder':
        return (
          <ConfirmDialog
            title={`Delete ${quote(dialog.folder.name)}?`}
            message="The folder and its subfolders are removed. Files that are only in this folder are deleted too; files that are also in another folder are kept."
            confirmLabel="Delete folder"
            danger
            busy={dialog.busy}
            onCancel={close}
            onConfirm={runDeleteFolder}
          />
        );
      case 'create-folder':
        return (
          <NameDialog
            title={dialog.parentName ? `New folder in ${quote(dialog.parentName)}` : 'New folder'}
            confirmLabel="Create"
            busy={dialog.busy}
            error={dialog.error}
            onCancel={close}
            onSubmit={submitCreateFolder}
          />
        );
      case 'rename-folder':
        return (
          <NameDialog
            title={`Rename ${quote(dialog.folder.name)}`}
            initialValue={dialog.folder.name}
            confirmLabel="Rename"
            busy={dialog.busy}
            error={dialog.error}
            onCancel={close}
            onSubmit={submitRenameFolder}
          />
        );
      default:
        return null;
    }
  })();

  const NoticeIcon = notice?.kind === 'error' ? IoAlertCircleOutline : notice?.kind === 'success' ? IoCheckmarkCircle : IoInformationCircleOutline;

  return (
    <div
      className="fl-backdrop"
      onMouseDown={(e) => { backdropPressRef.current = e.target === e.currentTarget; }}
      onClick={(e) => {
        // Only a press that started AND ended on the backdrop closes: a text
        // selection dragged out of the browser doesn't, nor does a click while
        // a delete / move / rename / merge is still running.
        const pressedBackdrop = backdropPressRef.current;
        backdropPressRef.current = false;
        if (!pressedBackdrop || e.target !== e.currentTarget || isBusy()) return;
        requestClose();
      }}
    >
      <div
        ref={modalRef}
        className={`fl-modal${isCompany ? ' is-company' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="fl-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="fl-header">
          <div className="fl-header-icon" aria-hidden="true"><MdFolder size={20} /></div>
          <div className="fl-header-text">
            <h2 id="fl-title" className="fl-title">File Browser</h2>
            <p className="fl-subtitle">{isCompany
              ? `${companyName} library · shared with ${viewingUserLabel ? 'the team' : 'your team'}`
              : (viewingUserLabel ? `${viewingUserLabel}'s personal library · admin view` : 'Your personal library')}</p>
          </div>
          {hasCompany ? (
            <div className={`fl-scope${isCompany ? ' is-company' : ''}`} role="group" aria-label="Library">
              <button type="button" className={!isCompany ? 'is-on' : ''} aria-pressed={!isCompany} onClick={() => switchScope('personal')}>Personal</button>
              <button type="button" className={isCompany ? 'is-on' : ''} aria-pressed={isCompany} onClick={() => switchScope('company')} title={companyName}>{companyName}</button>
            </div>
          ) : null}
          <button
            type="button"
            className={`fl-icon-btn fl-settings-btn${view.id === 'settings' ? ' is-active' : ''}`}
            onClick={() => selectView({ id: 'settings' })}
            aria-label="Library settings"
            title="Library settings"
          >
            <IoSettingsOutline size={19} />
          </button>
          <button type="button" className="fl-icon-btn fl-close" onClick={requestClose} aria-label="Close file browser">
            <IoCloseOutline size={20} />
          </button>
        </header>

        <div className="fl-mobile-nav">
          <label className="fl-sr-only" htmlFor="fl-mobile-view">Show</label>
          <select id="fl-mobile-view" value={mobileValue} onChange={(e) => {
            const value = e.target.value;
            if (value.startsWith('folder:')) openFolder(value.slice(7));
            else selectView({ id: value });
          }}>
            {NAV_ITEMS.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            {/* Settings opens from the cog; while it's open the menu says where you are. */}
            {view.id === 'settings' ? <option value="settings" disabled>Library settings</option> : null}
            {mobileFolderOptions.length ? (
              <optgroup label="Folders">
                {mobileFolderOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </optgroup>
            ) : null}
          </select>
        </div>

        {notice ? (
          <div className={`fl-notice is-${notice.kind}`} role={notice.kind === 'error' ? 'alert' : 'status'} key={notice.id}>
            <NoticeIcon size={17} aria-hidden="true" />
            <span className="fl-notice-text">{notice.message}</span>
            {notice.actions.map((action) => (
              <button key={action.label} type="button" className="fl-btn fl-btn-sm" onClick={() => { setNotice(null); action.run(); }}>
                {action.label}
              </button>
            ))}
            <button type="button" className="fl-icon-btn fl-icon-btn-sm" onClick={() => setNotice(null)} aria-label="Dismiss message">
              <IoCloseOutline size={15} />
            </button>
          </div>
        ) : null}

        <div className={`fl-body${selectedFile ? ' has-detail' : ''}`}>
          <LibraryNav
            view={view}
            onSelectView={(next) => (next.id === 'folder' ? openFolder(next.folderId) : selectView(next))}
            folderIndex={folderIndex}
            treeState={tree}
            onRetryTree={loadTree}
            stats={stats}
            expanded={expanded}
            onToggleFolder={(id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))}
            folderActions={folderActions}
            onCreateFolder={() => setDialog({ type: 'create-folder', parentId: null })}
            dragActive={Boolean(dragFiles)}
            dropTargetId={dropTargetId}
            onDragOverTarget={setDropTargetId}
            onDragLeaveTarget={(key) => setDropTargetId((current) => (current === key ? null : current))}
            onDropOnTarget={handleDropOnTarget}
          />
          <main className="fl-main">{main}</main>
          {selectedFile ? (
            <FileDetailPanel
              file={selectedFile}
              client={client}
              revision={detailRevision}
              isMobile={isMobile}
              attachStatus={attachStatus[selectedFile.file_id]}
              canShareToCompany={canShareToCompany}
              onClose={() => setSelectedFile(null)}
              onRename={handleRenameFile}
              onMove={openMoveFiles}
              onDelete={openDeleteFiles}
              onAttach={onAttachFile ? handleAttach : null}
              onShareToCompany={handleShareFile}
              onOpenConversation={onOpenConversation}
              onOpenFolder={openFolder}
              onDownload={handleDownload}
              suggestionBusy={suggestionBusy[selectedFile.file_id]}
              onAcceptSuggestion={handleAcceptSuggestion}
              onDismissSuggestion={handleDismissSuggestion}
            />
          ) : null}
        </div>

        {dialogNode}
      </div>
    </div>
  );
}
